/**
 * SchedulerConfigOverview Component
 *
 * Dashboard summary widget showing scheduler configuration status.
 * Displays service area and blackout date counts for each franchise.
 *
 * Features:
 * - Summary cards for Hamilton and North York franchises
 * - Service area counts (total and active)
 * - Upcoming blackout dates count
 * - Last update timestamp
 * - Quick action buttons to navigate to tabs
 *
 * @example
 * <SchedulerConfigOverview />
 */

import React from 'react'
import { Link } from 'react-router-dom'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  MapPin,
  Calendar,
  Building2,
  CheckCircle,
  AlertCircle,
  ArrowRight,
} from 'lucide-react'
import { useServiceAreas } from '../hooks/useServiceAreas'
import { useUpcomingBlackouts } from '../hooks/useBlackoutDates'
import { getFranchiseLabel } from '../utils/schedulerFormatters'

export function SchedulerConfigOverview() {
  // Fetch all service areas
  const { data: allAreas, isLoading: areasLoading } = useServiceAreas()

  // Fetch upcoming blackouts for each franchise
  const { data: hamiltonBlackouts, isLoading: hamiltonBlackoutsLoading } =
    useUpcomingBlackouts('hamilton')
  const { data: northyorkBlackouts, isLoading: northyorkBlackoutsLoading } =
    useUpcomingBlackouts('northyork')

  const isLoading =
    areasLoading || hamiltonBlackoutsLoading || northyorkBlackoutsLoading

  // Calculate stats for each franchise
  const hamiltonAreas =
    allAreas?.areas.filter((area) => area.franchise_region === 'hamilton') || []
  const northyorkAreas =
    allAreas?.areas.filter((area) => area.franchise_region === 'northyork') ||
    []

  const hamiltonStats = {
    totalAreas: hamiltonAreas.length,
    activeAreas: hamiltonAreas.filter((a) => a.is_active).length,
    upcomingBlackouts: hamiltonBlackouts?.totalCount || 0,
  }

  const northyorkStats = {
    totalAreas: northyorkAreas.length,
    activeAreas: northyorkAreas.filter((a) => a.is_active).length,
    upcomingBlackouts: northyorkBlackouts?.totalCount || 0,
  }

  // Get last updated timestamp (most recent update across all areas)
  const lastUpdated = React.useMemo(() => {
    if (!allAreas?.areas.length) return null

    const timestamps = allAreas.areas
      .map((area) => new Date(area.updated_at).getTime())
      .sort((a, b) => b - a)

    return new Date(timestamps[0])
  }, [allAreas])

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse">
          <div className="h-8 bg-muted rounded w-1/3 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2].map((i) => (
              <div key={i} className="h-48 bg-muted rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">
            Configuration Overview
          </h2>
          <p className="text-muted-foreground">
            Scheduler configuration summary for all franchises
          </p>
        </div>
        {lastUpdated && (
          <div className="text-sm text-muted-foreground">
            Last updated:{' '}
            {lastUpdated.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
            })}
          </div>
        )}
      </div>

      {/* Franchise Summary Cards */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Hamilton Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-primary" />
                <CardTitle>{getFranchiseLabel('hamilton')}</CardTitle>
              </div>
              <Badge variant="outline">HQ</Badge>
            </div>
            <CardDescription>
              Service area configuration for Hamilton franchise
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Service Areas */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10 text-primary">
                  <MapPin className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-sm font-medium">Service Areas</p>
                  <p className="text-xs text-muted-foreground">
                    {hamiltonStats.activeAreas} active
                  </p>
                </div>
              </div>
              <div className="text-2xl font-bold">
                {hamiltonStats.totalAreas}
              </div>
            </div>

            {/* Upcoming Blackouts */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
              <div className="flex items-center gap-3">
                <div
                  className={`p-2 rounded-lg ${
                    hamiltonStats.upcomingBlackouts > 0
                      ? 'bg-warning/10 text-warning'
                      : 'bg-success/10 text-success'
                  }`}
                >
                  {hamiltonStats.upcomingBlackouts > 0 ? (
                    <AlertCircle className="h-4 w-4" />
                  ) : (
                    <CheckCircle className="h-4 w-4" />
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium">Upcoming Blackouts</p>
                  <p className="text-xs text-muted-foreground">
                    Scheduled dates
                  </p>
                </div>
              </div>
              <div className="text-2xl font-bold">
                {hamiltonStats.upcomingBlackouts}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="pt-2 flex gap-2">
              <Button variant="outline" size="sm" className="flex-1" asChild>
                <Link to="/scheduler?tab=service-areas&franchise=hamilton">
                  <MapPin className="mr-2 h-3 w-3" />
                  View Areas
                </Link>
              </Button>
              <Button variant="outline" size="sm" className="flex-1" asChild>
                <Link to="/scheduler?tab=blackout-dates&franchise=hamilton">
                  <Calendar className="mr-2 h-3 w-3" />
                  View Dates
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* North York Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-primary" />
                <CardTitle>{getFranchiseLabel('northyork')}</CardTitle>
              </div>
              <Badge variant="outline">Franchise</Badge>
            </div>
            <CardDescription>
              Service area configuration for North York franchise
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Service Areas */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10 text-primary">
                  <MapPin className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-sm font-medium">Service Areas</p>
                  <p className="text-xs text-muted-foreground">
                    {northyorkStats.activeAreas} active
                  </p>
                </div>
              </div>
              <div className="text-2xl font-bold">
                {northyorkStats.totalAreas}
              </div>
            </div>

            {/* Upcoming Blackouts */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
              <div className="flex items-center gap-3">
                <div
                  className={`p-2 rounded-lg ${
                    northyorkStats.upcomingBlackouts > 0
                      ? 'bg-warning/10 text-warning'
                      : 'bg-success/10 text-success'
                  }`}
                >
                  {northyorkStats.upcomingBlackouts > 0 ? (
                    <AlertCircle className="h-4 w-4" />
                  ) : (
                    <CheckCircle className="h-4 w-4" />
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium">Upcoming Blackouts</p>
                  <p className="text-xs text-muted-foreground">
                    Scheduled dates
                  </p>
                </div>
              </div>
              <div className="text-2xl font-bold">
                {northyorkStats.upcomingBlackouts}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="pt-2 flex gap-2">
              <Button variant="outline" size="sm" className="flex-1" asChild>
                <Link to="/scheduler?tab=service-areas&franchise=northyork">
                  <MapPin className="mr-2 h-3 w-3" />
                  View Areas
                </Link>
              </Button>
              <Button variant="outline" size="sm" className="flex-1" asChild>
                <Link to="/scheduler?tab=blackout-dates&franchise=northyork">
                  <Calendar className="mr-2 h-3 w-3" />
                  View Dates
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Combined Stats Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Overall Statistics</CardTitle>
          <CardDescription>
            Combined metrics across all franchises
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Total Areas</p>
              <p className="text-3xl font-bold">
                {hamiltonStats.totalAreas + northyorkStats.totalAreas}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Active Areas</p>
              <p className="text-3xl font-bold text-success">
                {hamiltonStats.activeAreas + northyorkStats.activeAreas}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm text-muted-foreground">
                Upcoming Blackouts
              </p>
              <p className="text-3xl font-bold text-warning">
                {hamiltonStats.upcomingBlackouts +
                  northyorkStats.upcomingBlackouts}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Franchises</p>
              <p className="text-3xl font-bold">2</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Navigation to Tabs */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Button variant="outline" className="flex-1" asChild>
          <Link to="/scheduler?tab=service-areas">
            <MapPin className="mr-2 h-4 w-4" />
            Manage Service Areas
            <ArrowRight className="ml-auto h-4 w-4" />
          </Link>
        </Button>
        <Button variant="outline" className="flex-1" asChild>
          <Link to="/scheduler?tab=blackout-dates">
            <Calendar className="mr-2 h-4 w-4" />
            Manage Blackout Dates
            <ArrowRight className="ml-auto h-4 w-4" />
          </Link>
        </Button>
      </div>
    </div>
  )
}
